(() => {
  if (customElements.get('lens-selector')) return;

  const TITLES = {
    1: 'Choose Your Prescription Type',
    2: 'How Would You Like to Add Your Prescription?',
    3: 'Choose Lens Group',
    4: 'Choose Your Lenses',
    5: 'Select Color',
    6: 'Select Coating',
    7: 'Review your selections'
  };

  const PRESCRIPTION_METHOD_TITLES = Object.freeze({
    upload: 'Upload a Photo of Your Prescription',
    manual: 'Fill It Out Myself'
  });

  const RX_RANGES = {
    single: { sph: [-15, 15, 0.25], cyl: [-8, 8, 0.25] },
    reading: { sph: [0, 6, 0.25], cyl: null }
  };

  const DEFAULT_PRESCRIPTION_TYPE_ORDER = ['single', 'reading', 'non'];
  const ORDERABLE_PRESCRIPTION_TYPES = new Set(DEFAULT_PRESCRIPTION_TYPE_ORDER);
  const LENS_PRODUCT_TYPE_BY_HANDLE = Object.freeze({
    'single-vision-lens': 'single',
    'reading-lens': 'reading',
    'non-prescription-lens': 'non'
  });
  const LENS_SELECTOR_MODES = new Set(['normal', 'no_sun', 'color']);
  const PRESCRIPTION_TYPES_BY_MODE = {
    normal: DEFAULT_PRESCRIPTION_TYPE_ORDER,
    no_sun: DEFAULT_PRESCRIPTION_TYPE_ORDER,
    color: DEFAULT_PRESCRIPTION_TYPE_ORDER
  };

  const DISABLED_LENS_GROUPS = new Set([
    'Polarized Sunglass',
    'Blush',
    'Photochromic Blush'
  ]);

  const GROUP_META = {
    Clear: {
      description: 'Traditional, transparent lenses perfect for everyday use',
      image: 'https://cdn.shopify.com/s/files/1/0788/6205/7758/files/3_e8fea182-a662-4b07-90ea-1a3186767844.png?v=1706858152&width=200'
    },
    'Blue Light Blocking': {
      description: 'Protect your eyes from blue light of digital screens',
      image: 'https://cdn.shopify.com/s/files/1/0788/6205/7758/files/4_e878a0b9-96ef-450f-9730-39a089210606.png?v=1706858152&width=200'
    },
    Photochromic: {
      description: 'Lenses darken in outdoor light and clear indoors',
      image: 'https://cdn.shopify.com/s/files/1/0788/6205/7758/files/5.png?v=1706858152&width=200'
    },
    Sunglass: {
      description: 'Tinted to provide UV protection and reduce brightness',
      image: 'https://cdn.shopify.com/s/files/1/0788/6205/7758/files/1_77aea531-8a2b-4dc1-b8ef-f3ac1d9bac2c.png?v=1706858152&width=200'
    }
  };

  const REFRACTION_META = {
    '1.61': ['1.61 Standard Index Lens', 'Significantly thinner and lighter (up to 25%) compared to low index.'],
    '1.67': ['1.67 High Index Lens', 'Significantly thinner and lighter (up to 30%) compared to low index.'],
    '1.74': ['1.74 Superior Ultra Index Lens', 'Significantly thinner and lighter (up to 35%) compared to low index.'],
    '1.74 Plus': ['1.74 Plus Superior Ultra Index Lens', 'Contains all functions of the 1.74 lens with additional thinning.']
  };

  const COATING_META = {
    Standard: 'Anti-scratch; Anti-reflective; UV-protective; Impact-resistant',
    'Blue Light Blocking': 'All of Standard Plus; Protect your eyes from blue light',
    'Super Hydrophobic Coating': 'All of Standard Plus; Anti-fingerprint and water-resistant'
  };

  const COLOR_SWATCH_BACKGROUNDS = Object.freeze({
    Gray: '#7A8085',
    Brown: '#8A5B3D',
    Green: '#5F7868',
    Blue: '#6289AA',
    Purple: '#876A96',
    Golden: '#D4AF37',
    Pink: '#D98FA5',
    Tea: '#A77854',
    Silver: '#BFC5CB',
    'Black Red': '#7D1F2B'
  });

  const tintSwatchStyle = (color, colorBackgrounds) => {
    const swatch = colorBackgrounds[color] || COLOR_SWATCH_BACKGROUNDS[color] || '';
    if (!swatch) return '';
    return `--cus-lens-swatch:${swatch}`;
  };

  const GRADIENT_SWATCH_BACKGROUNDS = Object.freeze({
    Gray: '#656B70',
    Brown: '#936346',
    Green: '#68806F',
    Blue: '#7397B4',
    Purple: '#9077A0'
  });

  const TINT_OPTIONS = [
    { group: 'Photochromic', refraction: '1.61', title: '', colors: ['Gray', 'Brown', 'Purple', 'Blue', 'Pink'] },
    { group: 'Photochromic', refraction: '1.67', title: '', colors: ['Gray', 'Brown', 'Purple', 'Blue'] },
    { group: 'Photochromic', refraction: '1.74', title: '', colors: ['Gray'] },
    { group: 'Sunglass', refraction: '1.61', title: 'Basic', strengths: ['80', '40', '20'], colors: ['Gray', 'Brown', 'Green', 'Blue', 'Purple', 'Pink'] },
    { group: 'Sunglass', refraction: '1.61', title: 'Gradient', colors: ['Gray', 'Brown', 'Green', 'Blue', 'Purple'] },
    { group: 'Sunglass', refraction: '1.67', title: 'Basic', strengths: ['80', '40', '20'], colors: ['Gray', 'Brown', 'Green', 'Blue', 'Purple', 'Pink'] },
    { group: 'Sunglass', refraction: '1.67', title: 'Gradient', colors: ['Gray', 'Brown', 'Green', 'Purple', 'Blue'] }
  ];

  const unique = (items) => [...new Set(items.filter((item) => item !== null && item !== undefined))];
  const escapeHtml = (value) => String(value).replace(/[&<>'"]/g, (char) => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', "'": '&#039;', '"': '&quot;'
  }[char]));

  class LensSelector extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.config = JSON.parse(this.querySelector('[data-lens-config]').textContent);
      this.modal = this.querySelector('[data-lens-modal]');
      this.choiceDialog = this.querySelector('[data-choice-dialog]');
      this.warningDialog = this.querySelector('[data-warning-dialog]');
      this.lastFocused = null;
      this.warningConfirm = null;
      this.resetState();
      this.addEventListener('click', this.handleClick.bind(this));
      this.addEventListener('input', this.handleInput.bind(this));
      this.addEventListener('change', this.handleChange.bind(this));
      this.keydownHandler = this.handleKeydown.bind(this);
      document.addEventListener('keydown', this.keydownHandler);
      this.updateAvailablePrescriptionTypes();
    }

    disconnectedCallback() {
      document.removeEventListener('keydown', this.keydownHandler);
    }

    resetState() {
      this.state = {
        step: 1,
        prescriptionType: null,
        prescriptionMethod: null,
        lensProduct: null,
        group: null,
        refraction: null,
        tint: null,
        strength: null,
        color: null,
        coating: null,
        lensVariant: null,
        prescriptionFile: null,
        rx: {
          od: { sph: null, cyl: null, axis: null },
          os: { sph: null, cyl: null, axis: null },
          pd: { value: null, right: null, left: null, dual: false }
        }
      };
    }

    getMode() {
      return LENS_SELECTOR_MODES.has(this.config.mode) ? this.config.mode : 'normal';
    }

    isLocalInventoryTest() {
      return ['127.0.0.1', 'localhost'].includes(window.location.hostname);
    }

    isVariantAvailable(variant) {
      return Boolean(variant && (this.isLocalInventoryTest() || variant.available !== false));
    }

    getPdRange(type) {
      if (type === 'value') return [46, 80, 1];
      if (type === 'right') return [23, 40, 0.5];
      return [25, 40, 0.5];
    }

    getPrescriptionTypeCards() {
      const requestedCards = Array.isArray(this.config.prescriptionTypeCards)
        ? this.config.prescriptionTypeCards
        : DEFAULT_PRESCRIPTION_TYPE_ORDER.map((type) => ({ type }));
      const seen = new Set();
      const allowedTypes = new Set(PRESCRIPTION_TYPES_BY_MODE[this.getMode()]);

      return requestedCards.map((card) => {
        const hasProductHandle = Boolean(card?.productHandle);
        const productType = LENS_PRODUCT_TYPE_BY_HANDLE[card?.productHandle];
        const type = hasProductHandle ? productType : card?.type;
        return type ? { ...card, type } : null;
      }).filter((card) => {
        const type = card?.type;
        if (!ORDERABLE_PRESCRIPTION_TYPES.has(type) || !allowedTypes.has(type) || seen.has(type)) return false;
        seen.add(type);
        return true;
      });
    }

    isPrescriptionTypeAvailable(type) {
      const product = this.config.lensProducts[type];
      return Boolean(product?.variants?.some((variant) => this.isVariantAvailable(variant)));
    }

    updateAvailablePrescriptionTypes() {
      const container = this.querySelector('[data-lens-prescription-types]');
      const visibleCards = this.getPrescriptionTypeCards().filter((card) => this.isPrescriptionTypeAvailable(card.type));
      const cardsByType = new Map(visibleCards.map((card) => [card.type, card]));

      this.querySelectorAll('[data-prescription-type]').forEach((button) => {
        const type = button.dataset.prescriptionType;
        const product = this.config.lensProducts[type];
        const card = cardsByType.get(type);
        const title = button.querySelector('.cus-lens-step-item-info-title');
        const image = button.querySelector('.cus-lens-step-item-image img');
        const badge = button.querySelector('[data-prescription-hot]');
        const badgeImage = badge?.querySelector('[data-prescription-hot-image]');
        const badgeText = badge?.querySelector('[data-prescription-hot-text]');
        const displayTitle = product?.title || title?.textContent?.trim() || '';
        const displayImage = card?.image || product?.image;
        if (displayTitle && title) title.textContent = displayTitle;
        if (displayImage && image) {
          image.src = displayImage;
          image.alt = displayTitle || '';
        }
        if (badge) {
          const hotLabel = card?.hotLabel || 'HOT';
          const hotImage = card?.hotImage || '';
          // A configured image is itself an explicit HOT badge selection. Keep
          // the checkbox for text-only badges, while image-only configuration
          // should not silently remain hidden.
          const showHotBadge = Boolean(card?.hot || hotImage);
          badge.hidden = !showHotBadge;
          badge.classList.toggle('cus-lens-step-item-badge--image', Boolean(hotImage));
          if (badgeText) badgeText.textContent = hotLabel;
          else badge.textContent = hotLabel;
          if (badgeImage) {
            badgeImage.hidden = !hotImage;
            badgeImage.alt = hotLabel;
            if (hotImage) badgeImage.src = hotImage;
            else badgeImage.removeAttribute('src');
          }
          if (badgeText) badgeText.hidden = Boolean(hotImage);
        }
        button.hidden = !cardsByType.has(type);
      });

      if (container) {
        visibleCards.forEach((card) => {
          const button = container.querySelector(`[data-prescription-type="${card.type}"]`);
          if (button) container.append(button);
        });
      }

      return visibleCards.map((card) => card.type);
    }

    handleClick(event) {
      const actionElement = event.target.closest('[data-lens-action]');
      if (actionElement && this.contains(actionElement)) {
        const actions = {
          open: () => this.open(),
          close: () => this.close(),
          back: () => this.back(),
          'prescription-next': () => this.prescriptionNext(),
          'prescription-help': () => this.showPrescriptionHelp(),
          'pd-help': () => this.showPdHelp(),
          'toggle-more': () => this.toggleMore(),
          'remove-upload': () => this.removeUpload(),
          'close-choice': () => this.closeChoice(),
          'close-warning': () => this.closeWarning(),
          'warning-confirm': () => this.confirmWarning(),
          'warning-cancel': () => this.closeWarning(),
          'add-to-cart': () => this.addToCart()
        };
        if (actions[actionElement.dataset.lensAction]) actions[actionElement.dataset.lensAction]();
        return;
      }

      const typeButton = event.target.closest('[data-prescription-type]');
      if (typeButton && this.contains(typeButton)) {
        this.selectPrescriptionType(typeButton.dataset.prescriptionType);
        return;
      }

      const methodButton = event.target.closest('[data-prescription-method]');
      if (methodButton && this.contains(methodButton)) {
        this.selectPrescriptionMethod(methodButton.dataset.prescriptionMethod);
        return;
      }

      const selectButton = event.target.closest('[data-lens-select]');
      if (selectButton && this.contains(selectButton)) {
        this.openValueChoice(selectButton);
        return;
      }

      const choice = event.target.closest('[data-choice-value]');
      if (choice && this.contains(choice)) {
        this.chooseValue(Number(choice.dataset.choiceValue), choice.textContent.trim());
        return;
      }

      const groupButton = event.target.closest('[data-lens-group]');
      if (groupButton && this.contains(groupButton)) {
        this.state.group = groupButton.dataset.lensGroup;
        this.showRefractions();
        return;
      }

      const refractionButton = event.target.closest('[data-lens-refraction]');
      if (refractionButton && this.contains(refractionButton)) {
        this.state.refraction = refractionButton.dataset.lensRefraction;
        this.state.tint = null;
        this.state.strength = null;
        this.state.color = null;
        this.showTints();
        return;
      }

      const tintHeader = event.target.closest('[data-tint-header]');
      if (tintHeader && this.contains(tintHeader)) {
        this.selectTint(tintHeader.dataset.tintHeader);
        return;
      }

      const tintColor = event.target.closest('[data-tint-color]');
      if (tintColor && this.contains(tintColor)) {
        this.selectTintColor(tintColor);
        return;
      }

      const tintConfirm = event.target.closest('[data-tint-confirm]');
      if (tintConfirm && this.contains(tintConfirm)) {
        this.confirmTint(tintConfirm.dataset.tintConfirm);
        return;
      }

      const coatingButton = event.target.closest('[data-lens-coating]');
      if (coatingButton && this.contains(coatingButton)) {
        this.selectCoating(coatingButton.dataset.lensCoating);
      }
    }

    handleInput(event) {
      if (!event.target.matches('[data-axis-input]')) return;
      const input = event.target;
      const value = Math.min(180, Math.max(0, parseInt(input.value || '0', 10)));
      input.value = value;
      this.state.rx[input.dataset.eye].axis = value;
      input.closest('td').classList.remove('require');
    }

    handleChange(event) {
      if (event.target.matches('[data-pd-toggle]')) {
        this.state.rx.pd.dual = event.target.checked;
        this.state.rx.pd.value = null;
        this.state.rx.pd.right = null;
        this.state.rx.pd.left = null;
        this.querySelector('[data-pd-single]').hidden = event.target.checked;
        this.querySelector('[data-pd-dual]').hidden = !event.target.checked;
        this.querySelector('[data-pd-single]').classList.remove('require');
        this.querySelector('[data-pd-dual]').classList.remove('require');
        this.resetPdLabels();
      }

      if (event.target.matches('[data-prescription-upload]')) {
        this.handleUpload(event.target.files[0]);
      }

      if (event.target.matches('[data-tint-strength]')) {
        this.state.strength = event.target.value;
        const card = event.target.closest('[data-tint-card]');
        card.querySelector('[data-strength-value]').textContent = `${event.target.value}%`;
        this.updateTintConfirm(card);
      }
    }

    handleKeydown(event) {
      if (event.key !== 'Escape') return;
      if (!this.warningDialog.hidden) this.closeWarning();
      else if (!this.choiceDialog.hidden) this.closeChoice();
      else if (!this.modal.hidden) this.close();
    }

    open() {
      if (!this.isFrameVariantAvailable()) {
        this.showWarning('Frame unavailable', 'The selected frame is currently sold out. Please choose an available frame option.');
        return;
      }
      if (!this.updateAvailablePrescriptionTypes().length) {
        this.showWarning('Lens products unavailable', 'The required lens products are currently unavailable. Please contact us for assistance.');
        return;
      }
      this.lastFocused = document.activeElement;
      this.resetState();
      this.resetFormUi();
      this.showStep(1);
      this.modal.hidden = false;
      this.modal.setAttribute('aria-hidden', 'false');
      document.body.classList.add('cus-lens-open');
      this.querySelector('[data-lens-action="close"]').focus();
    }

    close() {
      this.modal.hidden = true;
      this.modal.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('cus-lens-open');
      if (this.lastFocused) this.lastFocused.focus();
    }

    showStep(step) {
      this.state.step = step;
      this.querySelectorAll('[data-lens-step]').forEach((element) => {
        element.hidden = Number(element.dataset.lensStep) !== step;
      });
      this.querySelector('[data-lens-step-title]').textContent = TITLES[step];
      this.querySelectorAll('[data-lens-progress]').forEach((bar) => {
        bar.style.width = `${(step / 7) * 100}%`;
      });
      this.querySelectorAll('[data-lens-action="back"]').forEach((button) => {
        button.style.visibility = step === 1 ? 'hidden' : 'visible';
      });
      this.querySelector('.cus-lens-prescription-main').scrollTop = 0;
    }

    back() {
      const previous = { 1: 0, 2: 1, 3: this.state.prescriptionType === 'non' ? 1 : 2, 4: 3, 5: 4, 6: this.hasTintOptions() ? 5 : 4, 7: 6 };
      if (this.state.step === 1) {
        this.close();
        return;
      }
      if (this.state.step === 2) {
        const details = this.querySelector('[data-prescription-details]');
        if (details && !details.hidden) this.showPrescriptionMethodChoices(true);
        else this.showStep(1);
        return;
      }
      if (this.state.step === 3 && this.state.prescriptionType !== 'non') {
        this.showStep(2);
        if (this.state.prescriptionMethod) this.selectPrescriptionMethod(this.state.prescriptionMethod);
        else this.showPrescriptionMethodChoices();
        return;
      }
      this.showStep(previous[this.state.step]);
    }

    selectPrescriptionType(type) {
      const product = this.config.lensProducts[type];
      if (!product || !product.variants || !product.variants.length) {
        this.showWarning('Lens option unavailable', `The ${type} lens product is not configured or has no variants.`);
        return;
      }
      this.state.prescriptionType = type;
      this.state.lensProduct = product;
      this.resetPrescription();
      if (type === 'non') this.showGroups();
      else {
        this.configurePrescriptionUi();
        this.showStep(2);
      }
    }

    selectPrescriptionMethod(method) {
      if (!['upload', 'manual'].includes(method)) return;
      const previousMethod = this.state.prescriptionMethod;
      if (previousMethod && previousMethod !== method) {
        if (method === 'manual') this.removeUpload();
        else this.resetManualPrescriptionValues();
      }
      this.state.prescriptionMethod = method;
      this.querySelectorAll('[data-prescription-method]').forEach((button) => {
        button.classList.toggle('selected', button.dataset.prescriptionMethod === method);
      });
      this.querySelector('[data-prescription-methods]').hidden = true;
      this.querySelector('[data-prescription-details]').hidden = false;
      this.querySelector('[data-prescription-manual-fields]').hidden = method !== 'manual';
      this.querySelector('[data-prescription-upload-wrap]').hidden = method !== 'upload';
      this.querySelector('[data-lens-step-title]').textContent = PRESCRIPTION_METHOD_TITLES[method];
    }

    resetPrescription() {
      this.resetManualPrescriptionValues();
      this.state.rx.pd = { value: null, right: null, left: null, dual: false };
      this.querySelector('[data-pd-toggle]').checked = false;
      this.querySelector('[data-pd-single]').hidden = false;
      this.querySelector('[data-pd-dual]').hidden = true;
      this.resetPdLabels();
      this.querySelectorAll('.require').forEach((element) => element.classList.remove('require'));
      this.resetPrescriptionMethodUi();
    }

    resetManualPrescriptionValues() {
      ['od', 'os'].forEach((eye) => {
        this.state.rx[eye] = { sph: null, cyl: null, axis: null };
      });
      this.querySelectorAll('[data-lens-select][data-eye="od"], [data-lens-select][data-eye="os"]').forEach((button) => {
        button.querySelector('span').textContent = '0.00';
      });
      this.querySelectorAll('[data-axis-input]').forEach((input) => {
        input.value = 0;
        input.disabled = true;
      });
      this.querySelectorAll('[data-rx-eye] .require').forEach((element) => element.classList.remove('require'));
    }

    resetPrescriptionMethodUi() {
      this.showPrescriptionMethodChoices();
    }

    showPrescriptionMethodChoices(preserveSelection = false) {
      if (!preserveSelection) this.state.prescriptionMethod = null;
      const methods = this.querySelector('[data-prescription-methods]');
      const details = this.querySelector('[data-prescription-details]');
      const manualFields = this.querySelector('[data-prescription-manual-fields]');
      const uploadWrap = this.querySelector('[data-prescription-upload-wrap]');
      if (methods) methods.hidden = false;
      if (details) details.hidden = true;
      if (manualFields) manualFields.hidden = false;
      if (uploadWrap) uploadWrap.hidden = true;
      if (!preserveSelection) {
        this.querySelectorAll('[data-prescription-method]').forEach((button) => button.classList.remove('selected'));
      }
      const title = this.querySelector('[data-lens-step-title]');
      if (title && this.state.step === 2) title.textContent = TITLES[2];
    }

    configurePrescriptionUi() {
      const range = RX_RANGES[this.state.prescriptionType];
      this.querySelectorAll('[data-rx-column="cyl"], [data-rx-column="axis"]').forEach((element) => { element.hidden = !range.cyl; });
      this.querySelector('.cus-lens-step2').classList.toggle('reading', this.state.prescriptionType === 'reading');
    }

    openValueChoice(button) {
      const { eye, valueType } = button.dataset;
      let range;
      let title;
      if (eye === 'pd') {
        range = this.getPdRange(valueType);
        title = 'PD - Pupillary Distance';
      } else {
        range = RX_RANGES[this.state.prescriptionType]?.[valueType];
        title = `${eye === 'od' ? 'RIGHT EYE - OD' : 'LEFT EYE - OS'}`;
      }
      if (!range) return;

      this.choiceTarget = button;
      button.setAttribute('aria-expanded', 'true');
      this.querySelector('[data-choice-title]').textContent = title;
      this.querySelector('[data-choice-subtitle]').textContent = valueType.toUpperCase();
      const current = eye === 'pd' ? this.state.rx.pd[valueType] : this.state.rx[eye][valueType];
      const values = [];
      const decimals = range[2] < 1 ? (valueType === 'sph' || valueType === 'cyl' ? 2 : 1) : 0;
      for (let value = range[0]; value <= range[1] + 0.001; value += range[2]) {
        const normalized = Number(value.toFixed(2));
        const sign = normalized > 0 && eye !== 'pd' ? '+' : '';
        values.push({
          value: normalized,
          label: `${sign}${normalized.toFixed(decimals)}`,
          selected: current === normalized
        });
      }

      const valuesContainer = this.querySelector('[data-choice-values]');
      const splitSignedValues = eye !== 'pd' && ['sph', 'cyl'].includes(valueType) && this.state.prescriptionType === 'single';
      const choiceButton = ({ value, label, selected }) =>
        `<button type="button" class="${selected ? 'selected' : ''}" data-choice-value="${value}" aria-pressed="${selected}">${label}</button>`;

      if (splitSignedValues) {
        const columns = [
          { name: 'Negative values', values: values.filter((item) => item.value < 0).reverse() },
          { name: 'Zero and positive values', values: values.filter((item) => item.value >= 0) }
        ].filter((column) => column.values.length);
        valuesContainer.dataset.choiceLayout = 'split';
        valuesContainer.innerHTML = columns.map((column) =>
          `<div class="cus-lens-choice-column" data-choice-column role="group" aria-label="${column.name}">${column.values.map(choiceButton).join('')}</div>`
        ).join('');
      } else {
        valuesContainer.dataset.choiceLayout = 'grid';
        valuesContainer.innerHTML = values.map(choiceButton).join('');
      }

      this.choiceDialog.hidden = false;
      this.choiceDialog.setAttribute('aria-hidden', 'false');
      requestAnimationFrame(() => {
        const selected = this.choiceDialog.querySelector('[data-choice-value].selected');
        const target = selected || this.choiceDialog.querySelector('[data-choice-value]');
        if (!target) return;
        const scroller = target.closest('[data-choice-column]') || valuesContainer;
        scroller.scrollTop = Math.max(0, target.offsetTop - scroller.offsetTop);
        target.focus({ preventScroll: true });
      });
    }

    chooseValue(value, label) {
      const { eye, valueType } = this.choiceTarget.dataset;
      if (eye === 'pd') {
        this.state.rx.pd[valueType] = value;
        if ((valueType === 'value' && (value < 50 || value > 70)) ||
            (['right', 'left'].includes(valueType) && this.state.rx.pd.right && this.state.rx.pd.left &&
             (this.state.rx.pd.right + this.state.rx.pd.left < 50 || this.state.rx.pd.right + this.state.rx.pd.left > 70))) {
          this.showWarning('Pupillary Distance (PD)', 'Most people have a total PD between 50 and 70. Please make sure the entered value is correct.');
        }
      } else {
        this.state.rx[eye][valueType] = value;
        if (valueType === 'cyl') {
          const axis = this.querySelector(`[data-axis-input][data-eye="${eye}"]`);
          axis.disabled = value === 0;
          if (value === 0) {
            axis.value = 0;
            this.state.rx[eye].axis = 0;
          }
        }
        if (valueType === 'sph') this.warnOppositeSpheres();
        if (['sph', 'cyl'].includes(valueType)) this.warnOppositeSigns(eye);
      }
      this.choiceTarget.querySelector('span').textContent = label;
      this.choiceTarget.closest('td, .cus-lens-pd-fields').classList.remove('require');
      this.closeChoice();
    }

    closeChoice() {
      this.choiceDialog.hidden = true;
      this.choiceDialog.setAttribute('aria-hidden', 'true');
      if (this.choiceTarget) {
        this.choiceTarget.setAttribute('aria-expanded', 'false');
        this.choiceTarget.focus();
      }
    }

    warnOppositeSpheres() {
      if (this.state.rx.od.sph * this.state.rx.os.sph < 0) {
        this.showWarning('Warning', 'For most people, both eyes have either negative or positive SPH values. Please confirm your prescription.');
      }
    }

    warnOppositeSigns(eye) {
      const values = this.state.rx[eye];
      if (values.sph * values.cyl < 0) {
        this.showWarning('Warning', 'It is unusual for SPH and CYL to use opposite signs. Please confirm your prescription.');
      }
    }

    prescriptionNext() {
      if (!this.state.prescriptionMethod) return;

      if (this.state.prescriptionMethod === 'upload') {
        if (!this.state.prescriptionFile) {
          this.showWarning('Upload your prescription', 'Please upload a clear photo of your prescription before continuing.');
          return;
        }
        const pd = this.state.rx.pd;
        const singlePdMissing = !pd.dual && !pd.value;
        const dualPdMissing = pd.dual && (!pd.right || !pd.left);
        this.querySelector('[data-pd-single]').classList.toggle('require', singlePdMissing);
        this.querySelector('[data-pd-dual]').classList.toggle('require', dualPdMissing);
        if (singlePdMissing || dualPdMissing) {
          this.showWarning('Pupillary Distance (PD)', 'Please select your PD before continuing.');
          return;
        }
        this.showGroups();
        return;
      }

      let valid = true;
      ['od', 'os'].forEach((eye) => {
        const values = this.state.rx[eye];
        const axisInput = this.querySelector(`[data-axis-input][data-eye="${eye}"]`);
        const requiresAxis = values.cyl !== null && values.cyl !== 0 && !values.axis;
        axisInput.closest('td').classList.toggle('require', requiresAxis);
        if (requiresAxis) valid = false;
      });

      const pd = this.state.rx.pd;
      if (pd.dual) {
        ['right', 'left'].forEach((side) => {
          const button = this.querySelector(`[data-lens-select][data-eye="pd"][data-value-type="${side}"]`);
          button.classList.toggle('require', !pd[side]);
          if (!pd[side]) valid = false;
        });
      }
      if (!valid) return;

      if ((!pd.dual && !pd.value) || (pd.dual && !pd.right && !pd.left)) {
        this.showWarning(
          'Pupillary Distance (PD)',
          "PD is required for prescription eyeglasses. You may continue without it and the store can contact you for the measurement.",
          () => this.showGroups()
        );
        return;
      }
      this.showGroups();
    }

    showGroups() {
      const variants = this.state.lensProduct.variants.filter((variant) => this.isVariantAvailable(variant));
      let groups = unique(variants.map((variant) => variant.option1)).filter(
        (group) => group !== 'Gradient Sunglass' && !DISABLED_LENS_GROUPS.has(group)
      );
      if (this.getMode() === 'color') groups = groups.filter((group) => !['Clear', 'Blue Light Blocking', 'Sunglass'].includes(group));

      const container = this.querySelector('[data-lens-groups]');
      container.innerHTML = groups.map((group) => this.optionCard({
        data: `data-lens-group="${escapeHtml(group)}"`,
        title: group,
        description: GROUP_META[group]?.description || '',
        image: GROUP_META[group]?.image,
        hot: this.config.lensGroupHotCards?.[group]
      })).join('');
      if (!groups.length) {
        this.showWarning('Lens groups unavailable', 'The selected lens product has no available Lens Group variants for this frame mode.');
        return;
      }
      this.showStep(3);
    }

    showRefractions() {
      const variants = this.state.lensProduct.variants.filter((variant) => this.isVariantAvailable(variant) && variant.option1 === this.state.group);
      let values = unique(variants.map((variant) => String(variant.option2)));

      const power = Math.max(
        Math.abs(this.state.rx.od.sph || 0) + Math.abs(this.state.rx.od.cyl || 0),
        Math.abs(this.state.rx.os.sph || 0) + Math.abs(this.state.rx.os.cyl || 0)
      );
      const more = [];
      if (power > 4 && power <= 7 && values.includes('1.61')) more.push('1.61');
      if (power > 7) {
        values = values.filter((value) => value !== '1.61');
        if (values.includes('1.67')) more.push('1.67');
      }
      values = values.filter((value) => !more.includes(value));
      if (!(power >= 9 && this.state.prescriptionType === 'single' && this.state.group !== 'Sunglass')) {
        values = values.filter((value) => value !== '1.74 Plus');
      }

      if (!values.length && !more.length) {
        this.showWarning('Lens index unavailable', 'The selected Lens Group has no available Refraction variants for this prescription.');
        return;
      }

      this.renderRefractionList(this.querySelector('[data-lens-refractions]'), values, false);
      this.renderRefractionList(this.querySelector('[data-lens-refractions-more]'), more, true);
      const toggle = this.querySelector('[data-lens-action="toggle-more"]');
      toggle.hidden = more.length === 0;
      this.querySelector('[data-lens-refractions-more]').hidden = true;
      this.showStep(4);
    }

    renderRefractionList(container, values, notRecommended) {
      container.innerHTML = values.map((value) => {
        const meta = REFRACTION_META[value] || [`${value} Index Lens`, ''];
        return this.optionCard({
          data: `data-lens-refraction="${escapeHtml(value)}"`,
          title: meta[0],
          description: meta[1],
          image: 'https://cdn.shopify.com/s/files/1/0788/6205/7758/files/6_b86edbf9-50c3-4fb2-8380-010bb13bf879.png?v=1706858370&width=200',
          hot: this.config.refractionHotCards?.[value],
          badge: notRecommended ? 'Not Recommend' : ''
        });
      }).join('');
    }

    toggleMore() {
      const container = this.querySelector('[data-lens-refractions-more]');
      container.hidden = !container.hidden;
    }

    hasTintOptions() {
      return this.getTintOptions().length > 0;
    }

    getTintOptions() {
      const refraction = String(this.state.refraction);
      return TINT_OPTIONS
        .filter((option) => option.group === this.state.group && option.refraction === refraction)
        .map((option) => ({
          title: option.title,
          description: option.title === 'Basic'
            ? 'Choose from these classic colors to make your own sunglasses'
            : option.title === 'Gradient'
              ? 'Fashionable gradient lenses that go from dark to light'
              : '',
          strengths: option.strengths,
          colors: option.colors
        }));
    }

    showTints() {
      const options = this.getTintOptions();
      if (!options.length) {
        this.showCoatings();
        return;
      }
      const container = this.querySelector('[data-lens-tints]');
      container.innerHTML = options.map((option, index) => {
        const title = option.title || 'Color';
        const colorBackgrounds = option.title === 'Gradient' ? GRADIENT_SWATCH_BACKGROUNDS : COLOR_SWATCH_BACKGROUNDS;
        return `<div class="cus-lens-step5-list-item" data-tint-card="${escapeHtml(option.title)}">
          <button type="button" class="cus-lens-step5-list-item-header" data-tint-header="${escapeHtml(option.title)}">
            <span class="cus-lens-step5-list-item-title">${escapeHtml(title)}</span>
            <span class="cus-lens-step5-list-item-desc">${escapeHtml(option.description)}</span>
          </button>
          <div class="cus-lens-step5-list-item-body" ${options.length === 1 || index === 0 ? '' : 'hidden'}>
            ${option.strengths ? `<div class="cus-lens-step-color-strength"><div class="cus-lens-step-color-title">Tint Strength: <span data-strength-value></span></div><div class="cus-lens-step-color-strength-list">${option.strengths.map((strength) => `<label><input type="radio" name="LensTintStrength-${this.config.sectionId}" value="${strength}" data-tint-strength> ${strength}%</label>`).join('')}</div></div>` : ''}
            <div class="cus-lens-step-color-colors">
              <div class="cus-lens-step-color-heading">
                <div>
                  <div class="cus-lens-step-color-title">Color <span data-color-value aria-live="polite">Choose a color</span></div>
                  <p class="cus-lens-step-color-help">Choose the finish that suits your style.</p>
                </div>
              </div>
              <div class="cus-lens-step-color-colors-list">${option.colors.map((color) => `<div class="cus-lens-color-option"><button type="button" class="cus-lens-step-color-colors-list-item" data-tint-color="${escapeHtml(color)}" title="${escapeHtml(color)}" aria-label="Select ${escapeHtml(color)}" aria-pressed="false" style="${tintSwatchStyle(color, colorBackgrounds)}"><span class="visually-hidden">${escapeHtml(color)}</span></button><span class="cus-lens-color-option-label">${escapeHtml(color)}</span></div>`).join('')}</div>
            </div>
            <button type="button" class="cus-lens-primary-btn cus-lens-tint-confirm" data-tint-confirm="${escapeHtml(option.title)}" disabled>Confirm</button>
          </div>
        </div>`;
      }).join('');
      this.state.tint = options[0].title;
      this.showStep(5);
    }

    selectTint(title) {
      this.state.tint = title;
      this.state.strength = null;
      this.state.color = null;
      this.querySelectorAll('[data-tint-card]').forEach((card) => {
        const selected = card.dataset.tintCard === title;
        card.querySelector('.cus-lens-step5-list-item-body').hidden = !selected;
        card.querySelector('.cus-lens-step5-list-item-header').classList.toggle('selected', selected);
        if (selected) {
          card.querySelectorAll('input').forEach((input) => { input.checked = false; });
          card.querySelectorAll('[data-tint-color]').forEach((button) => {
            button.classList.remove('selected');
            button.setAttribute('aria-pressed', 'false');
          });
          card.querySelectorAll('[data-strength-value]').forEach((element) => { element.textContent = ''; });
          card.querySelectorAll('[data-color-value]').forEach((element) => { element.textContent = 'Choose a color'; });
          this.updateTintConfirm(card);
        }
      });
    }

    selectTintColor(button) {
      const card = button.closest('[data-tint-card]');
      card.querySelectorAll('[data-tint-color]').forEach((item) => {
        item.classList.remove('selected');
        item.setAttribute('aria-pressed', 'false');
      });
      button.classList.add('selected');
      button.setAttribute('aria-pressed', 'true');
      this.state.color = button.dataset.tintColor;
      card.querySelector('[data-color-value]').textContent = this.state.color;
      this.updateTintConfirm(card);
    }

    updateTintConfirm(card) {
      const needsStrength = Boolean(card.querySelector('[data-tint-strength]'));
      card.querySelector('[data-tint-confirm]').disabled = !this.state.color || (needsStrength && !this.state.strength);
    }

    confirmTint(title) {
      const card = this.querySelector(`[data-tint-card="${CSS.escape(title)}"]`);
      const button = card.querySelector('[data-tint-confirm]');
      if (!button.disabled) this.showCoatings();
    }

    getVariantGroup() {
      if (this.state.tint !== 'Gradient') return this.state.group;

      const hasGradientVariant = this.state.lensProduct.variants.some((variant) =>
        this.isVariantAvailable(variant) &&
        variant.option1 === 'Gradient Sunglass' &&
        String(variant.option2) === String(this.state.refraction)
      );

      return hasGradientVariant ? 'Gradient Sunglass' : this.state.group;
    }

    showCoatings() {
      const option1 = this.getVariantGroup();
      const variants = this.state.lensProduct.variants.filter((variant) =>
        this.isVariantAvailable(variant) && variant.option1 === option1 && String(variant.option2) === String(this.state.refraction)
      );
      const coatings = unique(variants.map((variant) => variant.option3));
      const container = this.querySelector('[data-lens-coatings]');
      container.innerHTML = coatings.map((coating) => {
        const variant = variants.find((item) => item.option3 === coating);
        return this.optionCard({
          data: `data-lens-coating="${escapeHtml(coating)}"`,
          title: coating,
          description: COATING_META[coating] || '',
          price: this.formatMoney(variant.price)
        });
      }).join('');
      if (!coatings.length) {
        this.showWarning('Lens variant unavailable', 'No lens variant matches the selected group, refraction, tint and coating combination.');
        return;
      }
      this.showStep(6);
    }

    selectCoating(coating) {
      const option1 = this.getVariantGroup();
      const variant = this.state.lensProduct.variants.find((item) =>
        this.isVariantAvailable(item) && item.option1 === option1 && String(item.option2) === String(this.state.refraction) && item.option3 === coating
      );
      if (!variant) return;
      this.state.coating = coating;
      this.state.lensVariant = variant;
      if (this.renderReview()) this.showStep(7);
    }

    getFrameVariant() {
      const form = document.getElementById(this.config.productFormId);
      const variantId = Number(form?.querySelector('[name="id"]')?.value || 0);
      if (!variantId && this.isLocalInventoryTest()) return this.config.product.variants[0] || null;
      if (!variantId) return null;
      return this.config.product.variants.find((variant) => Number(variant.id) === variantId) || null;
    }

    isFrameVariantAvailable(frame = this.getFrameVariant()) {
      return this.isVariantAvailable(frame);
    }

    renderReview() {
      const frame = this.getFrameVariant();
      if (!this.isFrameVariantAvailable(frame)) {
        this.showWarning('Frame unavailable', 'The selected frame is currently sold out. Please choose an available frame option.');
        return false;
      }
      const prescriptionReview = this.querySelector('[data-review-prescription]');
      prescriptionReview.hidden = this.state.prescriptionType === 'non';
      if (this.state.prescriptionType !== 'non') {
        ['od', 'os'].forEach((eye) => {
          const row = this.querySelector(`[data-review-eye="${eye}"]`);
          const values = this.state.rx[eye];
          row.querySelector('[data-field="sph"]').textContent = this.formatPrescriptionValue(values.sph);
          row.querySelector('[data-field="cyl"]').textContent = this.formatPrescriptionValue(values.cyl);
          row.querySelector('[data-field="axis"]').textContent = values.axis ? `x${values.axis}` : '--';
        });
        const pd = this.state.rx.pd;
        const pdCells = {
          od: this.querySelector('[data-review-pd-cell="od"]'),
          os: this.querySelector('[data-review-pd-cell="os"]')
        };
        Object.values(pdCells).forEach((cell) => {
          cell.hidden = false;
          cell.rowSpan = 1;
          cell.textContent = '';
        });
        if (pd.dual) {
          pdCells.od.textContent = pd.right || '--';
          pdCells.os.textContent = pd.left || '--';
        } else {
          pdCells.od.rowSpan = 2;
          pdCells.od.textContent = pd.value || '--';
          pdCells.os.hidden = true;
        }
      }
      const info = [];
      if (this.state.prescriptionMethod) {
        info.push(['Prescription:', this.state.prescriptionMethod === 'upload' ? 'Photo upload' : 'Entered manually']);
      }
      if (this.state.strength) info.push(['Tint Strength:', `${this.state.strength}%`]);
      if (this.state.color) info.push(['Color:', this.state.color]);
      this.querySelector('[data-review-info]').innerHTML = info.map(([title, value]) => `<div class="cus-lens-step7-info-item"><span>${escapeHtml(title)}</span><span>${escapeHtml(value)}</span></div>`).join('');
      this.querySelector('[data-frame-title]').textContent = frame.name || `${this.config.product.title} - ${frame.title}`;
      this.querySelector('[data-frame-price]').textContent = this.formatMoney(frame.price);
      this.querySelector('[data-lens-product-title]').textContent = this.state.lensVariant.name || `${this.state.lensProduct.title} - ${this.state.lensVariant.title}`;
      this.querySelector('[data-lens-price]').textContent = this.formatMoney(this.state.lensVariant.price);
      this.querySelector('[data-review-subtotal]').textContent = this.formatMoney(frame.price + this.state.lensVariant.price);
      this.setError();
      return true;
    }

    async addToCart() {
      const submit = this.querySelector('[data-lens-action="add-to-cart"]');
      if (submit.disabled) return;
      const frame = this.getFrameVariant();
      if (!this.isFrameVariantAvailable(frame)) {
        this.setError('The selected frame is currently sold out. Please choose an available frame option.');
        return;
      }
      submit.disabled = true;
      submit.classList.add('spin');
      this.setError();
      try {
        const cart = await this.fetchJson(this.config.routes.cartJson);
        const currentFrame = this.getFrameVariant();
        if (!this.isFrameVariantAvailable(currentFrame) || Number(currentFrame.id) !== Number(frame.id)) {
          throw new Error('The selected frame is currently sold out or has changed. Please review your frame options.');
        }
        const packageNumbers = cart.items
          .map((item) => Number(item.properties?.Package))
          .filter((value) => Number.isFinite(value));
        const packageNumber = Math.max(0, ...packageNumbers) + 1;
        const lensProperties = this.buildLensProperties(packageNumber);
        const frameProperties = { Title: this.config.product.title, Package: packageNumber };

        if (this.state.prescriptionFile) {
          const lensData = new FormData();
          lensData.append('id', this.state.lensVariant.id);
          lensData.append('quantity', '1');
          Object.entries(lensProperties).forEach(([key, value]) => lensData.append(`properties[${key}]`, value));
          lensData.append('properties[Prescription Paper]', this.state.prescriptionFile, this.state.prescriptionFile.name);
          const addedLens = await this.fetchJson(this.config.routes.cartAdd, { method: 'POST', body: lensData, headers: { Accept: 'application/json' } });
          try {
            await this.postCartItems([{ id: frame.id, quantity: 1, properties: frameProperties }]);
          } catch (frameError) {
            if (addedLens.key) {
              try {
                await this.removeCartItem(addedLens.key);
              } catch (rollbackError) {
                throw new Error(`${frameError.message} The lens item could not be removed automatically; please review your cart.`);
              }
            }
            throw frameError;
          }
        } else {
          await this.postCartItems([
            { id: this.state.lensVariant.id, quantity: 1, properties: lensProperties },
            { id: frame.id, quantity: 1, properties: frameProperties }
          ]);
        }

        document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh'));
        if (document.querySelector('.js-cart-drawer')) {
          document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open', { detail: { opener: submit } }));
        } else {
          window.location.href = this.config.routes.cart;
        }
        this.close();
      } catch (error) {
        this.setError(error.message || 'Unable to add the frame and lenses to your cart.');
      } finally {
        submit.disabled = false;
        submit.classList.remove('spin');
      }
    }

    buildLensProperties(packageNumber) {
      const properties = {
        Title: this.state.lensProduct.title,
        'OD Right': this.buildEyeProperty(this.state.rx.od),
        'OS Left': this.buildEyeProperty(this.state.rx.os),
        PD: this.buildPdProperty(),
        Package: packageNumber
      };
      if (this.state.strength) properties.STRENGTH = this.state.strength;
      if (this.state.color) properties.COLOR = this.state.color;
      const note = this.querySelector('[data-order-note]').value.trim();
      if (note) properties.Note = note;
      return properties;
    }

    buildEyeProperty(values) {
      if (this.state.prescriptionType === 'non') return '-- | -- | --';
      const axis = values.axis ? `x${values.axis}` : '--';
      return `${this.formatPrescriptionValue(values.sph)} | ${this.formatPrescriptionValue(values.cyl)} | ${axis}`;
    }

    buildPdProperty() {
      const pd = this.state.rx.pd;
      if (pd.dual && pd.right && pd.left) return `Right:${pd.right} | Left:${pd.left}`;
      if (pd.value) return `Single PD | ${pd.value}`;
      return '--';
    }

    async postCartItems(items) {
      return this.fetchJson(this.config.routes.cartAdd, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items })
      });
    }

    async removeCartItem(key) {
      return this.fetchJson(this.config.routes.cartChange, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: key, quantity: 0 })
      });
    }

    async fetchJson(url, options = {}) {
      const response = await fetch(url, options);
      let data;
      try { data = await response.json(); } catch (error) { data = {}; }
      if (!response.ok) throw new Error(data.description || data.message || `Request failed (${response.status})`);
      return data;
    }

    handleUpload(file) {
      if (!file) return;
      if (file.size > 2 * 1024 * 1024) {
        this.querySelector('[data-prescription-upload]').value = '';
        this.showWarning('File is too large', 'Prescription images must not exceed 2 MB.');
        return;
      }
      this.state.prescriptionFile = file;
      const preview = this.querySelector('[data-upload-preview]');
      this.querySelector('[data-upload-preview-image]').src = URL.createObjectURL(file);
      this.querySelector('[data-upload-filename]').textContent = file.name;
      preview.hidden = false;
    }

    removeUpload() {
      this.state.prescriptionFile = null;
      this.querySelector('[data-prescription-upload]').value = '';
      const image = this.querySelector('[data-upload-preview-image]');
      if (image.src.startsWith('blob:')) URL.revokeObjectURL(image.src);
      image.removeAttribute('src');
      this.querySelector('[data-upload-preview]').hidden = true;
    }

    showWarning(title, body, confirm = null) {
      this.warningConfirm = confirm;
      this.querySelector('[data-warning-title]').textContent = title;
      this.querySelector('[data-warning-body]').textContent = body;
      this.warningDialog.hidden = false;
      this.warningDialog.setAttribute('aria-hidden', 'false');
      this.warningDialog.querySelector('[data-lens-action="warning-confirm"]').focus();
    }

    confirmWarning() {
      const callback = this.warningConfirm;
      this.closeWarning();
      if (callback) callback();
    }

    closeWarning() {
      this.warningDialog.hidden = true;
      this.warningDialog.setAttribute('aria-hidden', 'true');
      this.warningConfirm = null;
    }

    showPrescriptionHelp() {
      this.showWarning('Prescription Info', 'SPH is the lens strength. CYL and AXIS describe astigmatism.');
    }

    showPdHelp() {
      this.showWarning('PD - Pupillary Distance', 'PD is the distance in millimeters between the centers of your pupils. Use one total number or two monocular values from your prescription.');
    }

    resetFormUi() {
      this.removeUpload();
      this.querySelector('[data-order-note]').value = '';
      this.querySelectorAll('[data-prescription-type]').forEach((button) => button.classList.remove('selected'));
      this.resetPrescriptionMethodUi();
      this.updateAvailablePrescriptionTypes();
    }

    resetPdLabels() {
      this.querySelector('[data-lens-select][data-value-type="value"] span').textContent = 'PD';
      this.querySelector('[data-lens-select][data-value-type="right"] span').textContent = 'Right PD';
      this.querySelector('[data-lens-select][data-value-type="left"] span').textContent = 'Left PD';
    }

    hotBadgeMarkup(hot) {
      const hotImage = hot?.hotImage || '';
      const hotLabel = hot?.hotLabel || 'HOT';
      if (!hot?.hot && !hotImage) return '';
      if (hotImage) {
        return `<span class="cus-lens-step-item-badge cus-lens-step-item-badge--image"><img src="${escapeHtml(hotImage)}" alt="${escapeHtml(hotLabel)}" width="120" height="28" loading="lazy"></span>`;
      }
      return `<span class="cus-lens-step-item-badge"><span>${escapeHtml(hotLabel)}</span></span>`;
    }

    optionCard({ data, title, description = '', image = '', price = '', badge = '', hot = null }) {
      const hasHotBadge = Boolean(hot?.hot || hot?.hotImage);
      return `<button type="button" class="cus-lens-step-item${image ? '' : ' cus-lens-step-item--no-image'}" ${data}>
        ${image ? `<span class="cus-lens-step-item-image"><img src="${escapeHtml(image)}" alt="" width="100" height="100" loading="lazy"></span>` : ''}
        <span class="cus-lens-step-item-info"><span class="cus-lens-step-item-info-title">${escapeHtml(title)}</span><span class="cus-lens-step-item-info-desc">${escapeHtml(description)}</span></span>
        ${price ? `<span class="cus-lens-step-item-price">${escapeHtml(price)}</span>` : ''}
        ${this.hotBadgeMarkup(hot)}
        ${badge ? `<span class="cus-lens-step-item-badge${hasHotBadge ? ' cus-lens-step-item-badge--secondary' : ''}">${escapeHtml(badge)}</span>` : ''}
      </button>`;
    }

    signed(value) {
      const number = Number(value || 0);
      return `${number > 0 ? '+' : ''}${number.toFixed(2)}`;
    }

    formatPrescriptionValue(value) {
      return value === null || value === undefined ? '--' : this.signed(value);
    }

    formatMoney(cents) {
      const amount = (Number(cents || 0) / 100).toFixed(2);
      const format = this.config.moneyFormat || '${{amount}}';
      return format
        .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, String(Math.round(Number(amount))))
        .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, amount.replace('.', ','))
        .replace(/\{\{\s*amount\s*\}\}/g, amount)
        .replace(/<[^>]+>/g, '');
    }

    setError(message = '') {
      const error = this.querySelector('[data-lens-error]');
      error.hidden = !message;
      error.textContent = message;
    }
  }

  customElements.define('lens-selector', LensSelector);
})();
